import React from 'react';
import { Form } from 'react-bootstrap';
import DatePicker from 'components/common/DatePicker';
import moment from 'moment';

// Services & Helpers
import BaseService from 'services/BaseService';
import GlobalStateService from 'services/GlobalStateService';
import OnboardingService from 'services/OnboardingService';
import BootboxHelper from 'helpers/BootboxHelper';

// Components
import Loader from 'components/common/Loader';
import OnboardingQuestionnaireResponseModal from 'components/instructor/OnboardingQuestionnaireResponseModal';

//-------------------------------------------------------------------------------------------------------------------

class Onboarding extends React.Component {

    constructor(props) {
        super(props);

        this.onboardingQuestionnaireResponseModalRef = React.createRef();

        this.state = {
            isLoading: true,
            isUploadingEvidence: {}
        };
    }

    componentDidMount() {
        this.load();
    }

    async load() {
        const instructor = await BaseService.callAPI('instructor/get-onboarding/' + this.props.instructor.id);
        const onboarding = await BaseService.callAPI('onboarding/get/' + this.props.instructor.id);
        this.setState({
            instructor,
            onboarding,
            isLoading: false
        });
    }

    async save() {
        const { setIsBusy, setIsChanged } = this.props;
        const { instructor } = this.state;
        setIsBusy(true);
        this.setState({ isLoading: true });
        await BaseService.callAPI('instructor/save-onboarding', instructor);
        setIsBusy(false);
        setIsChanged(false);
        this.setState({ isLoading: false });
        BootboxHelper.alert('Changes saved');
        this.load();
    }

    updateFields(values) {
        const instructor = { ...this.state.instructor };
        for (var fieldName in values) {
            const value = values[fieldName];
            instructor[fieldName] = value;
        }
        this.setState({ instructor });
        this.props.setIsChanged(true, () => this.save());
    }

    updateOnboardingStep(id, newValues) {
        const onboardingSteps = { ...this.state.instructor.onboardingSteps };
        if (!onboardingSteps[id]) {
            onboardingSteps[id] = {};
        }
        for (let fieldName in newValues) {
            const value = newValues[fieldName];
            onboardingSteps[id][fieldName] = value;

            if (fieldName == 'isCompleted') {
                if (value) {
                    if (!onboardingSteps[id].dateCompleted) {
                        onboardingSteps[id].dateCompleted = moment().format('YYYY-MM-DD');
                    }
                } else {
                    onboardingSteps[id].dateCompleted = null;
                }
            }
        }
        this.updateFields({ onboardingSteps });
    }
    
    async uploadEvidence(id, field) {
        if (field.files.length > 0) {
            // Set loading
            let isUploadingEvidence = { ...this.state.isUploadingEvidence };
            isUploadingEvidence[id] = true;
            this.setState({ isUploadingEvidence });

            // Upload file
            const file = field.files[0];
            const { guid } = await BaseService.postFile('upload/add', file, { type: 'OnboardingEvidence', instructorID: this.props.instructor.id });
            field.value = '';

            // Update on UI
            this.updateOnboardingStep(id, {
                evidenceFileName: file.name,
                uploadGUID: guid
            });

            // Unset loading
            isUploadingEvidence = { ...this.state.isUploadingEvidence };
            delete isUploadingEvidence[id];
            this.setState({ isUploadingEvidence });
        }
    }

    getIsOnboardingCompleted() {
        const { instructor } = this.state;
        if (!instructor.id) {
            return false;
        }
        const onboardingSteps = Object.keys(instructor.onboardingSteps).map(ios => instructor.onboardingSteps[ios]);
        for (let i = 0; i < onboardingSteps.length; i++) {
            const onboardingStep = onboardingSteps[i];
            if (!onboardingStep.isCompleted ||
                (onboardingStep.isDated && !onboardingStep.dateCompleted) ||
                (onboardingStep.isEvidenced && !onboardingStep.evidenceFileName)
            ) {
                return false;
            }
        }
        return true;
    }

    getOnboardingStatusMessage() {
        const { id, onboardingStatus, numStepsCompleted } = this.state.instructor;
        if (!id) return '';
        switch (onboardingStatus || '') {
            case 'NotInvited':
                return 'Not invited yet';
            case 'Questionnaire':
                return 'Questionnaire not completed';
            case 'AwaitQuestionnaireApproval':
                return 'Questionnaire awaiting approval';
            case 'AwaitInterview':
                return 'Awaiting interview';
            case 'SignContract':
                return 'Contract not signed';
            case 'CompleteOnboardingSteps':
                return `${(numStepsCompleted || 0)} / 10 steps completed`;
            default:
                return onboardingStatus;
        }
    }

    getOnboardingStatusClassName() {
        const { onboardingStatus } = this.state.instructor;
        switch (onboardingStatus || '') {
            case 'Questionnaire':
            case 'SignContract':
            case '':
                return 'instructor-action-req';
        }
        return '';
    }

    showOnboardingQuestionnaireResponse() {
        this.onboardingQuestionnaireResponseModalRef.current.show({
            id: this.props.instructor.id
        });
    }

    async sendOnboardingWelcomeEmail() {
        const confirm = await BootboxHelper.confirm('Send onboarding welcome email?');
        if (confirm) {
            this.setState({ isLoading: true });
            await OnboardingService.sendWelcomeEmail(this.state.instructor.id);
            this.setState({ isLoading: false });
            await BootboxHelper.alert('Onboarding welcome email sent');
            this.load();
        }
    }

    async confirmApproveOnboarding() {
        const confirm = await BootboxHelper.confirm('Update status and notify instructor?');
        if (confirm) {
            this.approveOnboarding();
        }
    }

    async approveOnboarding() {
        this.setState({ isLoading: true });
        await OnboardingService.approve(this.state.instructor.id);
        BootboxHelper.alert('Email sent');
        this.setState({
            isLoading: false
        }, () => {
            this.load();
        });
    }

    async confirmSkipOnboarding() {
        const confirm = await BootboxHelper.confirm('Are you sure you want to skip the onboarding process?');
        if (confirm) {
            this.setState({ isLoading: true });
            await OnboardingService.skip(this.state.instructor.id);
            BootboxHelper.alert('Onboarding process skipped');
            this.setState({
                isLoading: false
            }, () => {
                this.props.history.replace(`/instructor/${this.state.instructor.slug}`);
            });
        }
    }

    //-------------------------------------------------------------------------------------------------------------------
    //  Render
    //-------------------------------------------------------------------------------------------------------------------

    render() {
        const { instructor } = this.props;
        const { isLoading } = this.state;

        if (isLoading) {
            return (<Loader />);
        }

        return (<>

            {this.renderStatusAndActions()}
            
            {this.renderOnboardingSteps()}

            {instructor.status == 'Candidate' && this.renderOnboardingTable()}

            <OnboardingQuestionnaireResponseModal ref={this.onboardingQuestionnaireResponseModalRef} />

        </>);
    }

    renderStatusAndActions() {
        const { loginInfo } = this.props;
        const { instructor, onboarding } = this.state;

        if (!loginInfo.isVendorOrAdmin) {
            return null;
        }

        return (<>

            <h2>Onboarding Status</h2>
            <div className={`onboarding-status ${this.getOnboardingStatusClassName()}`}>
                {this.getOnboardingStatusMessage()}
            </div>

            <div className="onboarding-status-buttons">

                <button type="button" className="btn btn-secondary" onClick={e => {
                    e.preventDefault();
                    this.sendOnboardingWelcomeEmail();
                }}>
                    <span className="fa-solid fa-envelope"></span>{' '}
                    Send onboarding welcome email 
                    {!!instructor.dateSentWelcomeEmail && <>
                        {' '}(sent {moment(instructor.dateSentWelcomeEmail).format('DD/MM/YYYY')})
                    </>}
                </button>

                {onboarding && 
                    <button type="button" className="btn btn-secondary mt-3" onClick={() => this.showOnboardingQuestionnaireResponse()}>
                        <span className="fa fa-list" />{' '}
                        Show questionnaire response{' '}
                        {onboarding.dateSubmitted ? <> (submitted {moment(onboarding.dateSubmitted).format('DD/MM/YYYY')})</> : <>(not submitted yet)</>}
                    </button> 
                }

                {instructor.onboardingStatus == 'AwaitQuestionnaireApproval' &&
                    <button type="button" className="btn btn-secondary" onClick={e => {
                        e.preventDefault();
                        this.confirmApproveOnboarding();
                    }}>
                        <span className="fa-solid fa-user-graduate"></span>{' '}
                        Approve onboarding questionnaire
                    </button>
                }

                {instructor.onboardingStatus == 'AwaitInterview' &&
                    <button type="button" className="btn btn-secondary" onClick={e => {
                        e.preventDefault();
                        this.confirmApproveOnboarding();
                    }}>
                        <span className="fa-solid fa-handshake"></span>{' '}
                        Interview completed (NOTE: this will send the 'Sign Contract' link)
                    </button>
                }

                <button type="button" className="btn btn-secondary" onClick={e => {
                    e.preventDefault();
                    this.confirmSkipOnboarding();
                }}>
                    <span className="fa-solid fa-forward"></span>{' '}
                    Skip / complete onboarding process
                </button>

            </div>

        </>);
    }

    renderOnboardingSteps() {
        const { isEditMode, isChanged } = this.props;
        const { instructor } = this.state;

        if (instructor.type != 'Candidate' || !instructor.onboardingSteps) {
            return null;
        }

        // Get steps
        const onboardingSteps = Object.keys(instructor.onboardingSteps).map(ios => instructor.onboardingSteps[ios]);
        if (onboardingSteps.length == 0) {
            return null;
        }
        onboardingSteps.sort((a, b) => a.stepNumber - b.stepNumber);

        // Determine which steps to disable if any
        let disableFromStepNumber = 99999;
        for (var i = onboardingSteps.length - 1; i >= 0; i--) {
            const onboardingStep = onboardingSteps[i];
            if (onboardingStep.disableNextStepsUntilCompleted && !onboardingStep.isCompleted) {
                disableFromStepNumber = onboardingStep.stepNumber + 1;
            }
        }

        // Render
        return (<>

            <h2>Steps to complete the AAI onboarding process</h2>

            <ul className="text-list onboarding-steps-list">

                <li>
                    <div className="step-title">
                        Date contract signed with Go Courses
                    </div>
                    <div className="step-body">
                        {instructor.dateContractSigned ? moment(instructor.dateContractSigned).format('DD/MM/YYYY') : 'Not signed'}
                    </div>
                </li>
                
                {onboardingSteps.map(ios => {
                    const isDisabled = (ios.stepNumber >= disableFromStepNumber);
                    
                    return (
                        <li key={ios.id} className={isDisabled ? 'step-disabled' : ''}>

                            <div className="step-title">
                                {ios.title}
                            </div>

                            <div className="step-body">

                                {ios.description &&
                                    <div className="step-description" dangerouslySetInnerHTML={{ __html: ios.description }} />
                                }

                                {!isDisabled && <>
                                    <Form.Group>
                                        <Form.Check
                                            type="checkbox"
                                            id={`onboarding-step-${ios.id}`}
                                            readOnly={!isEditMode}
                                            checked={ios.isCompleted || false}
                                            onChange={e => this.updateOnboardingStep(ios.id, { isCompleted: e.target.checked })}
                                            label="I have completed this step"
                                            disabled={isDisabled}
                                        />
                                    </Form.Group>

                                    {this.renderOnboardingStep(ios)}

                                </>}

                            </div>

                        </li>
                    );
                })}
            </ul>

            {this.getIsOnboardingCompleted() ? <>
                {!isChanged &&
                    <div className="alert alert-success mt-5 mb-0">
                        Congratulations on completing all onboarding steps!
                        Go Courses have been notified and will be in touch shortly.
                    </div>
                }
            </> :
                onboardingSteps[onboardingSteps.length - 1].isCompleted && (
                    <div className="alert alert-danger mt-5 mb-0">
                        In order to complete the onboarding process,
                        please ensure that all steps are completed, dated and evidenced as appropriate.
                    </div>
                )
            }

        </>);
    }
    
    renderOnboardingStep(ios) {
        const {
            isUploadingEvidence
        } = this.state;

        if (ios.isCompleted) {
            if (ios.isDated) {
                return (
                    <Form.Group className="mt-3">
                        {ios.dateOrEvidenceLabel || 'Date of completion'}:
                        <DatePicker
                            className="form-control"
                            selected={ios.dateCompleted ? new Date(ios.dateCompleted) : null}
                            onChange={value => this.updateOnboardingStep(ios.id, { dateCompleted: value })}
                        />
                    </Form.Group>
                );
            }
            else if (ios.isEvidenced) {
                const loginInfo = GlobalStateService.getValue('loginInfo');

                if (loginInfo.isAdmin && ios.uploadGUID) {
                    return (
                        <a href={`/api/upload/get/${ios.uploadGUID}?isInline=true`} className="btn btn-secondary mt-2" target="_blank">
                            <span className="fa-solid fa-check"></span>{' '}{ios.evidenceFileName}
                        </a>
                    );
                }

                return (
                    <Form.Group className="evidence">
                        {ios.dateOrEvidenceLabel || 'Please provide certification as evidence'}:

                        <label className="btn btn-secondary upload-button">
                            {isUploadingEvidence[ios.id] ? <>
                                <Loader isInline />{' '}Uploading...
                            </> : <>
                                {ios.evidenceFileName ? <>
                                    <span className="fa-solid fa-check"></span>{' '}{ios.evidenceFileName}
                                </> : <>
                                    <span className="fa-solid fa-upload"></span>{' '}Upload
                                    <input
                                        type="file"
                                        accept="application/pdf"
                                        onChange={e => this.uploadEvidence(ios.id, e.target)}
                                    />
                                </>}
                            </>}
                        </label>
                    </Form.Group>
                );
            }
        }

        return null;
    }

    renderOnboardingTable() {
        return (<>

            <div className="onboarding-table-container">

                <table className="onboarding-table">
                    <thead>
                        <tr>
                            <th className="week-1">Week 1</th>
                            <th className="week-2">Week 2</th>
                            <th className="week-3">Week 3</th>
                            <th className="week-4">Week 4</th>
                            <th className="week-5">Week 5</th>
                            <th className="week-6">Week 6</th>
                            <th className="week-7">Week 7</th>
                            <th className="week-8">Week 8</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td className="week-1">Welcome to the AAI Program!!!</td>
                            <td className="week-2">Delivery Skills 1 &amp; 2</td>
                            <td className="week-3">Delivery Skills 3 &amp; 4</td>
                            <td className="week-4">Delivery Skills 5 &amp; 6</td>
                            <td className="week-5">Delivery Skills 7 &amp; 8</td>
                            <td className="week-6">Delivery Skills 9 &amp; 10</td>
                            <td className="week-7">Pass SA Associate Exam</td>
                            <td className="week-8">ICA Prep</td>
                        </tr>
                        <tr>
                            <td className="week-1">Meet the Team and Cohort</td>
                            <td className="week-2">AWS Builder Labs DIG</td>
                            <td className="week-3">WebEx &amp; vILT DIG</td>
                            <td className="week-4">What to Expect in the ICA</td>
                            <td className="week-5">AWS Messaging Guidelines</td>
                            <td className="week-6">Troubleshooting</td>
                            <td className="week-7" rowSpan="2">Review Delivery Skills and Baseline Self-evaluations</td>
                            <td className="week-8" rowSpan="3">ICA</td>
                        </tr>
                        <tr>
                            <td className="week-1">Review the Global AAI Onboarding Guide</td>
                            <td className="week-2">Record a Baseline Delivery for Mod 1-4 &amp; Self-evaluate</td>
                            <td className="week-3">Review benefits in the AAI Program Guide</td>
                            <td className="week-4">Record a Baseline Delivery for Mod 5-9 &amp; Self-evaluate</td>
                            <td className="week-5">Review Course Offerings in AAI Delivery Enablement Guide</td>
                            <td className="week-6">Record a Baseline DElivery for Mod 10-13 &amp; Self-evaluate</td>
                        </tr>
                        <tr>
                            <td className="week-1">Architecting on AWS DIG (Mod 0-4)</td>
                            <td className="week-2">Shadow Architecting on AWS - Day 1</td>
                            <td className="week-3">Architecting on AWS DIG (Mod 5-9)</td>
                            <td className="week-4">Shadow Architecting on AWS - Day 2</td>
                            <td className="week-5">Architecting on AWS DIG (Mod 10-13)</td>
                            <td className="week-6">Shadow Architecting on AWS - Day 3</td>
                            <td className="week-7">Record a final delivery for Mod 1-13</td>
                        </tr>
                    </tbody>
                    <tfoot>
                        <tr>
                            <td>2.5 days</td>
                            <td>2.5 days</td>
                            <td>2.5 days</td>
                            <td>2.5 days</td>
                            <td>2.5 days</td>
                            <td>2.5 days</td>
                            <td>3 days</td>
                            <td>4 days</td>
                        </tr>
                    </tfoot>
                </table>

            </div>

        </>);
    }
}

export default Onboarding;